// Pure mapping from schedule/appointment API shapes to FullCalendar
// EventInput objects (SchedulePage, Task 5). Kept out of the component so
// it's testable without mounting FullCalendar: background events
// (appointments) depend on FullCalendar's real slot-height layout
// (`slatCoords`) to render at all, which jsdom's fake layout engine never
// produces — so a DOM-rendering test can't observe them. Testing the
// mapping directly here is what actually exercises this logic.
import type { EventInput } from "@fullcalendar/core";
import type {
  AdminLocation,
  AdminProvider,
  AppointmentSummary,
  ScheduleBlock,
  ScheduleShift,
} from "../api/types.js";
import { colorForLocation, BLOCK_COLOR } from "./schedule-colors.js";

// The three FullCalendar event id prefixes this module mints
// ("shift:<id>", "block:<id>", "appt:<id>") — parsed back out by Task 6's
// select/eventClick/eventDrop handlers so they know what kind of entity a
// clicked or dragged event represents without a second lookup pass.
export type ScheduleEventKind = "shift" | "block" | "appt";

export function parseEventId(id: string): { kind: ScheduleEventKind; id: string } {
  const idx = id.indexOf(":");
  return { kind: id.slice(0, idx) as ScheduleEventKind, id: id.slice(idx + 1) };
}

// "Jane Doe" -> "JD" — used to disambiguate shifts by provider when the
// "all providers" view stacks every provider's shifts together.
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0]!.toUpperCase())
    .join("");
}

export type BuildScheduleEventsInput = {
  shifts: readonly ScheduleShift[];
  blocks: readonly ScheduleBlock[];
  appointments: readonly AppointmentSummary[];
  locationById: ReadonlyMap<string, AdminLocation>;
  providerById: ReadonlyMap<string, AdminProvider>;
  // true when the page is showing every provider at once — in that view
  // everything is read-only and shift titles get a provider-initials
  // prefix so overlapping shifts stay distinguishable.
  allView: boolean;
};

export function shiftsToEvents(
  shifts: readonly ScheduleShift[],
  locationById: ReadonlyMap<string, AdminLocation>,
  providerById: ReadonlyMap<string, AdminProvider>,
  allView: boolean,
): EventInput[] {
  return shifts.map((s) => {
    const loc = locationById.get(s.location_id);
    const locName = loc?.display_name ?? "Unknown location";
    const provider = providerById.get(s.provider_id);
    const title =
      allView && provider
        ? `${initials(provider.display_name)} · ${locName}`
        : locName;
    return {
      id: `shift:${s.id}`,
      title,
      start: `${s.shift_date}T${s.start_time}`,
      end: `${s.shift_date}T${s.end_time}`,
      backgroundColor: colorForLocation(loc?.slug ?? ""),
      editable: !allView,
    };
  });
}

export function blocksToEvents(
  blocks: readonly ScheduleBlock[],
  allView: boolean,
): EventInput[] {
  return blocks.map((b) => ({
    id: `block:${b.id}`,
    title: b.label ?? "Blocked",
    start: `${b.block_date}T${b.start_time}`,
    end: `${b.block_date}T${b.end_time}`,
    backgroundColor: BLOCK_COLOR,
    editable: !allView,
  }));
}

export function appointmentsToEvents(
  appointments: readonly AppointmentSummary[],
): EventInput[] {
  return appointments.map((a) => ({
    id: `appt:${a.id}`,
    display: "background",
    title: a.public_code,
    start: a.scheduled_start,
    end: a.scheduled_end,
    editable: false,
    classNames: ["appt-bg"],
  }));
}

export function buildScheduleEvents({
  shifts,
  blocks,
  appointments,
  locationById,
  providerById,
  allView,
}: BuildScheduleEventsInput): EventInput[] {
  return [
    ...shiftsToEvents(shifts, locationById, providerById, allView),
    ...blocksToEvents(blocks, allView),
    ...appointmentsToEvents(appointments),
  ];
}
